import { Link, usePage } from "@inertiajs/react"
import { MessageSquare } from "lucide-react"

import { Breadcrumbs } from "@/components/breadcrumbs"
import { NotificationBell } from "@/components/notification-bell"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu"
import { UserMenuContent } from "@/components/user-menu-content"
import { useInitials } from "@/hooks/use-initials"
import { t } from "@/lib/i18n"
import { cn } from "@/lib/utils"
import type { BreadcrumbItem, NavItem } from "@/types"

const mainNavItems: NavItem[] = [
  {
    title: t.navigation.home,
    href: "/inicio",
  },
  {
    title: t.navigation.search,
    href: "/buscar",
  },
  {
    title: t.navigation.opportunities,
    href: "/oportunidades",
  },
  {
    title: t.navigation.tryouts,
    href: "/pruebas",
  },
]

interface AppHeaderProps {
  breadcrumbs?: BreadcrumbItem[]
}

export function AppHeader({ breadcrumbs = [] }: AppHeaderProps) {
  const page = usePage()
  const { auth } = page.props
  const getInitials = useInitials()
  return (
    <>
      <div className="border-border bg-background safe-area-top sticky top-0 z-50 border-b">
        <div className="mx-auto flex h-16 items-center px-4 md:max-w-7xl">
          <Link href="/inicio" prefetch className="flex items-center space-x-2">
            <img
              src="/images/krown_logo_header.png"
              alt="KrownFT"
              className="h-8"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="ml-6 hidden h-full items-center space-x-6 md:flex">
            <NavigationMenu className="flex h-full items-stretch">
              <NavigationMenuList className="flex h-full items-stretch space-x-2">
                {mainNavItems.map((item, index) => (
                  <NavigationMenuItem
                    key={index}
                    className="relative flex h-full items-center"
                  >
                    <Link
                      href={item.href}
                      className={cn(
                        navigationMenuTriggerStyle(),
                        "text-muted-foreground h-9 cursor-pointer px-3 text-xs tracking-wider uppercase hover:bg-white/5 hover:text-white",
                        page.url === item.href && "text-white",
                      )}
                    >
                      {item.title}
                    </Link>
                    {page.url === item.href && (
                      <div className="bg-krown-accent absolute bottom-0 left-0 h-[3px] w-full translate-y-px"></div>
                    )}
                  </NavigationMenuItem>
                ))}
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          <div className="ml-auto flex items-center space-x-2">
            {auth.user && (
              <>
                <NotificationBell
                  unreadCount={auth.unread_notifications_count}
                  notifications={auth.recent_notifications}
                />
                {/* Messages icon hidden on mobile (bottom tab bar handles it) */}
                <Link
                  href="/mensajes"
                  className="relative hidden md:block"
                  aria-label="Mensajes"
                >
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-[34px] w-[34px] text-white hover:bg-white/5"
                  >
                    <MessageSquare className="h-5 w-5" />
                  </Button>
                  {auth.unread_messages_count > 0 && (
                    <span className="bg-krown-accent text-krown-800 absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold">
                      {auth.unread_messages_count}
                    </span>
                  )}
                </Link>
                {/* User avatar hidden on mobile (bottom tab bar has Perfil) */}
                <div className="hidden md:block">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="size-10 rounded-full p-1 hover:bg-white/5"
                      >
                        <Avatar className="size-8 overflow-hidden rounded-full">
                          <AvatarImage
                            src={auth.user.avatar_url ?? undefined}
                            alt={auth.user.full_name}
                          />
                          <AvatarFallback className="bg-krown-200 rounded-lg text-white">
                            {getInitials(auth.user.full_name)}
                          </AvatarFallback>
                        </Avatar>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56" align="end">
                      <UserMenuContent auth={auth} />
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {breadcrumbs.length > 1 && (
        <div className="border-sidebar-border/70 flex w-full border-b">
          <div className="mx-auto flex h-12 w-full items-center justify-start px-4 text-neutral-500 md:max-w-7xl">
            <Breadcrumbs breadcrumbs={breadcrumbs} />
          </div>
        </div>
      )}
    </>
  )
}
